import { useContext, useEffect, useState } from "react";
import { Card, Stack, Typography, Switch, Button, Chip, FormControl, FormLabel } from "@mui/joy";
import { NotificationsActiveRounded } from "@mui/icons-material";
import { LayoutContext } from "../context";
import { requestPush, isPushSupported, getPlatform, getIsStandalone } from "../push";
import { APIToggleNotifications, APITestPushNotification } from "../api";

export default function PushNotificationSettings() {
    const { token, account, setAccount } = useContext(LayoutContext);
    const [enabled, setEnabled] = useState(account?.notifications_enabled ?? true);
    const [perm, setPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');

    useEffect(() => {
        setEnabled(account?.notifications_enabled ?? true);
    }, [account?.notifications_enabled]);

    const supported = isPushSupported();
    const iosNeedInstall = getPlatform() === 'ios' && !getIsStandalone();

    const handleEnableClick = async () => {
        setBusy(true); setMsg('');
        const res = await requestPush();
        setPerm(typeof Notification !== 'undefined' ? Notification.permission : 'default');
        if (res.type === 'success') setMsg('Уведомления подключены ✅');
        else if (res.type === 'ios_need_install') setMsg('На iPhone: «Поделиться» → «На экран Домой», затем откройте как приложение и включите снова.');
        else if (res.type === 'permission_denied_permanent') setMsg('Уведомления заблокированы в браузере. Разрешите их для сайта в настройках сайта.');
        else if (res.type === 'no_vapid_key') setMsg('Не задан VAPID-ключ (см. src/push.js).');
        else if (res.type === 'not_supported') setMsg('Браузер не поддерживает push.');
        else setMsg('Не удалось подключить уведомления.');
        setBusy(false);
    };

    const handleToggle = async (e) => {
        const val = e.target.checked;
        setEnabled(val); setBusy(true);
        try {
            const r = await APIToggleNotifications(token, val);
            setAccount?.(prev => prev ? { ...prev, notifications_enabled: r.notifications_enabled } : prev);
        } catch { setEnabled(!val); }
        finally { setBusy(false); }
    };

    const handleTest = async () => {
        setBusy(true); setMsg('');
        try {
            const r = await APITestPushNotification(token);
            setMsg(r.status === 'sent' ? 'Тест отправлен 🔔' : 'Нет активной подписки на этом устройстве — нажмите «Включить».');
        } catch { setMsg('Ошибка отправки теста.'); }
        finally { setBusy(false); }
    };

    return (
        <Card variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <NotificationsActiveRounded />
                    <Typography level="title-md">Push-уведомления</Typography>
                </Stack>

                {!supported && (
                    <Typography level="body-sm" color="warning">
                        Ваш браузер не поддерживает push-уведомления.
                    </Typography>
                )}

                {supported && (
                    <>
                        <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                            <FormLabel>Получать уведомления</FormLabel>
                            <Switch checked={enabled} onChange={handleToggle} disabled={busy} />
                        </FormControl>

                        {iosNeedInstall && (
                            <Typography level="body-sm" color="neutral">
                                На iPhone добавьте сайт на экран «Домой», чтобы включить уведомления.
                            </Typography>
                        )}

                        {perm !== 'granted' ? (
                            <Button onClick={handleEnableClick} loading={busy}>
                                Включить уведомления на этом устройстве
                            </Button>
                        ) : (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip color="success" variant="soft">Разрешено</Chip>
                                <Button size="sm" variant="outlined" onClick={handleTest} loading={busy}>
                                    Отправить тест
                                </Button>
                            </Stack>
                        )}

                        {msg && <Typography level="body-sm">{msg}</Typography>}
                    </>
                )}
            </Stack>
        </Card>
    );
}